import React, { useState, useEffect, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { FaArrowRight, FaCheck, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebook, FaLinkedin, FaTag, FaAward, FaUsers, FaRocket, FaStar, FaTicketAlt, FaHeadset, FaPaperPlane, FaCheckCircle, FaEnvelopeOpen } from 'react-icons/fa';
import { MdRocketLaunch, MdSecurity, MdDevicesOther, MdAutoAwesome } from 'react-icons/md';
import { offerAPI, inquiryAPI, productAPI } from '../../utils/api';
import SEO from '../../components/shared/SEO';
import toast from 'react-hot-toast';

// Resolve backend upload URLs
const getApiBase = () =>
  process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${getApiBase()}${url}`;
  return url;
};

// Fix image URLs inside rich HTML content
const resolveHtmlImages = (html) => {
  if (!html) return '';
  return html.replace(/src="(\/uploads\/[^"]+)"/g, (match, path) => {
    return `src="${getApiBase()}${path}"`;
  });
};

// ─── Offers Page ──────────────────────────────────────────────────────────────
export function OffersPage() {
  const [offers, setOffers]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    offerAPI.getAll().then(r => setOffers(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO title="Special Offers & Promotions" description="Take advantage of Softlogic Australia's latest special offers, promotions and limited-time deals on our software solutions." url="/offers" />
      <div className="page-hero">
        <Container>
          <div className="tag-label"><FaTag /> Limited Time</div>
          <h1 className="mt-2" style={{ letterSpacing:'-0.02em', lineHeight:1.1 }}>
            <span style={{ color:'#ffffff' }}>Special </span>
            <span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent' }}>Offers</span>
          </h1>
          <p>Take advantage of our exclusive deals and promotions on industry-leading software solutions</p>
        </Container>
      </div>
      <section style={{ background:'var(--bg-white)', padding:'40px 0 60px' }}>
        <Container>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color:'var(--sky-dark)' }} /></div>
          ) : offers.length === 0 ? (
            <div className="text-center py-5">
              <div style={{ fontSize:'3rem', color:'#94a3b8' }}><FaTag /></div>
              <h5 style={{ color:'#060d1a', marginTop:'1rem' }}>No active offers right now</h5>
              <p style={{ color:'#64748b' }}>Check back soon for exciting promotions!</p>
              <Link to="/contact" className="btn-cyber mt-3" style={{ display:'inline-block', padding:'0.65rem 2rem' }}>Contact for Custom Quote</Link>
            </div>
          ) : (
            <>
              {/* Featured offers full-width */}
              {offers.filter(o=>o.is_featured).map((offer, i) => (
                <div key={offer.id} style={{ background:'var(--bg-white)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', overflow:'hidden', marginBottom:'1rem', display:'flex', flexWrap:'wrap', boxShadow:'var(--shadow-md)' }}>
                  {offer.cover_image && <div className="offer-featured-img" style={{ width:'40%', minHeight:280, flexShrink:0 }}>
                    <img src={resolveUrl(offer.cover_image)} alt={offer.title} loading="lazy" width="600" height="280" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>}
                  <div style={{ flex:1, padding:'2.5rem', display:'flex', flexDirection:'column', justifyContent:'flex-start' }}>
                    <div style={{ background:'var(--sky-bg)', border:'1px solid rgba(0,212,255,0.3)', color:'var(--sky-dark)', borderRadius:100, display:'inline-block', padding:'0.3rem 1rem', fontSize:'0.78rem', fontWeight:800, marginBottom:'1rem', width:'fit-content' }}>
                      ⭐ FEATURED OFFER {offer.discount_value ? `— ${offer.discount_type==='percent'?`${offer.discount_value}% OFF`:`$${offer.discount_value} OFF`}` : ''}
                    </div>
                    <h3 style={{ fontFamily:'var(--font-display)', color:'var(--text-h)', marginBottom:'0.5rem' }}>{offer.title}</h3>
                    {offer.subtitle && <p style={{ color:'var(--sky-dark)', fontWeight:500, marginBottom:'0.75rem' }}>{offer.subtitle}</p>}
                    {offer.description && <p style={{ color:'#64748b', lineHeight:1.8, marginBottom:'1.25rem', maxWidth:560 }}>{offer.description}</p>}
                    {offer.coupon_code && (
                      <div style={{ background:'var(--sky-bg)', border:'1px solid var(--sky-border)', borderRadius:10, padding:'0.6rem 1rem', display:'inline-flex', alignItems:'center', gap:10, marginBottom:'1.25rem', width:'fit-content' }}>
                        <span style={{ color:'#64748b', fontSize:'0.78rem', fontWeight:700 }}>USE CODE:</span>
                        <code style={{ color:'var(--sky-dark)', fontWeight:800, fontSize:'1.1rem', fontFamily:'var(--font-mono)' }}>{offer.coupon_code}</code>
                      </div>
                    )}
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <Link to={offer.cta_link || '/contact'} className="btn-cyber" style={{ padding:'0.7rem 2rem' }}>{offer.cta_text || 'Claim Offer'}</Link>
                      {offer.valid_until && <span style={{ color:'#64748b', fontSize:'0.82rem' }}>⏰ Expires: {new Date(offer.valid_until).toLocaleDateString('en-AU',{month:'long',day:'numeric',year:'numeric'})}</span>}
                    </div>
                  </div>
                </div>
              ))}

              {/* Regular offers grid */}
              <Row className="g-4">
                {offers.filter(o=>!o.is_featured).map((offer, i) => (
                  <Col key={offer.id} md={6} lg={4} data-aos-delay={i%3*70}>
                    <div style={{ background:'var(--bg-page)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', overflow:'hidden', height:'100%', transition:'all 0.3s' }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,212,255,0.3)';e.currentTarget.style.transform='translateY(-4px)';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.transform='none';}}>
                      {offer.cover_image && <img src={resolveUrl(offer.cover_image)} alt={offer.title} loading="lazy" width="400" height="180" className="offer-card-img" style={{ width:'100%', height:180, objectFit:'cover' }} />}
                      <div style={{ padding:'1.25rem' }}>
                        {offer.discount_value && (
                          <div style={{ background:'var(--sky-bg)', border:'1px solid var(--border)', color:'var(--sky-dark)', borderRadius:100, display:'inline-block', padding:'0.2rem 0.75rem', fontSize:'0.75rem', fontWeight:800, marginBottom:'0.75rem' }}>
                            {offer.discount_type==='percent' ? `${offer.discount_value}% OFF` : offer.discount_type==='fixed' ? `$${offer.discount_value} OFF` : 'SPECIAL OFFER'}
                          </div>
                        )}
                        <h6 style={{ fontFamily:'var(--font-display)', color:'#060d1a', marginBottom:'0.4rem' }}>{offer.title}</h6>
                        {offer.description && <p style={{ color:'#64748b', fontSize:'0.82rem', lineHeight:1.7, marginBottom:'1rem' }}>{offer.description.slice(0,120)}…</p>}
                        {offer.coupon_code && (
                          <div style={{ background:'var(--sky-bg)', border:'1px solid var(--sky-border)', borderRadius:8, padding:'0.4rem 0.75rem', display:'inline-flex', alignItems:'center', gap:8, marginBottom:'1rem' }}>
                            <span style={{ color:'#64748b', fontSize:'0.7rem' }}>CODE:</span>
                            <code style={{ color:'var(--sky-dark)', fontWeight:800, fontFamily:'var(--font-mono)' }}>{offer.coupon_code}</code>
                          </div>
                        )}
                        {offer.valid_until && <p style={{ color:'#64748b', fontSize:'0.75rem', marginBottom:'0.75rem' }}>⏰ Valid until {new Date(offer.valid_until).toLocaleDateString('en-AU',{month:'short',day:'numeric',year:'numeric'})}</p>}
                        <Link to={offer.cta_link || '/contact'} className="btn-cyber d-block text-center" style={{ padding:'0.55rem', fontSize:'0.85rem', borderRadius:8 }}>
                          {offer.cta_text || 'Get This Offer'}
                        </Link>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Container>
      </section>
    </>
  );
}

// ─── Contact / Inquiry Page ───────────────────────────────────────────────────
export function ContactPage() {
  const [form, setForm]         = useState({ full_name:'', email:'', phone:'', company:'', subject:'', message:'', product_id:'', product_name:'' });
  const [products, setProducts] = useState([]);
  const [submitting, setSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const captchaRef  = useRef(null);
  const [captchaOk, setCaptchaOk] = useState(false);

  useEffect(() => {
    productAPI.getAll({ limit:50 }).then(r => setProducts(r.data.data || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) { toast.error('Please fill all required fields'); return; }
    if (!captchaOk) { toast.error('Please complete the CAPTCHA verification'); return; }
    setSubmit(true);
    try {
      await inquiryAPI.submit(form);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitted(true);
      captchaRef.current?.reset();
      setCaptchaOk(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send. Please try again.');
      captchaRef.current?.reset();
      setCaptchaOk(false);
    }
    setSubmit(false);
  };

  if (submitted) {
    return (
      <>
        <SEO title="Contact Us" url="/contact" />
        <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#ffffff' }}>
          <div className="text-center" style={{ background:'#ffffff', borderRadius:20, padding:'3rem', boxShadow:'0 2px 24px rgba(0,0,0,0.06)', border:'1px solid #e2e8f0', maxWidth:560 }}>
            <div style={{ fontSize:'5rem', marginBottom:'1.5rem', color:'#16a34a' }}><FaCheckCircle /></div>
            <h2 style={{ fontFamily:'var(--font-display)', color:'#060d1a', marginBottom:'1rem' }}>Inquiry Sent!</h2>
            <p style={{ color:'#64748b', fontSize:'1.05rem', maxWidth:480, margin:'0 auto 2rem' }}>
              Thank you for reaching out! Our team will review your inquiry and contact you within 1–2 business days.
            </p>
            <div className="d-flex gap-3 justify-content-center">
              <Link to="/products" className="btn-cyber" style={{ padding:'0.7rem 2rem', border:'2px solid #0043cc' }}>View Solutions</Link>
              <Link to="/" className="btn-cyber" style={{ padding:'0.7rem 2rem', border:'2px solid #0043cc' }}>Back to Home</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title="Contact Us — Send an Inquiry" description="Contact Softlogic Australia for software inquiries, demos and support. Our team will respond within 1-2 business days." url="/contact" />
      <div className="page-hero">
        <Container>
          <div className="tag-label">Get in Touch</div>
          <h1 className="mt-2" style={{ letterSpacing:'-0.02em', lineHeight:1.1 }}>
            <span style={{ color:'#ffffff' }}>Contact </span>
            <span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent' }}>Us</span>
          </h1>
          <p>Have a question or ready to transform your business? We'd love to hear from you.</p>
        </Container>
      </div>
      <section className="section" style={{ background:'var(--bg-white)' }}>
        <Container>
          <Row className="g-5 align-items-stretch">
            <Col lg={7} className="d-flex">
              <div style={{ background:'#ffffff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-lg)', padding:'2.5rem', boxShadow:'0 2px 16px rgba(0,0,0,0.05)', width:'100%', display:'flex', flexDirection:'column' }}>
                <h4 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'0.5rem' }}>Send Us an Inquiry</h4>
                <p style={{ color:'#64748b', fontSize:'0.88rem', marginBottom:'1.75rem' }}>Fill in the form and our team will get back to you promptly. All inquiries are sent directly to our marketing team.</p>
                <form onSubmit={handleSubmit} className="form-light" style={{ flex:1, display:'flex', flexDirection:'column' }}>
                  <Row className="g-3" style={{ flex:1 }}>
                    {[['full_name','Full Name *','text'],['email','Email Address *','email'],['phone','Phone Number','tel'],['company','Company Name','text']].map(([k,p,t]) => (
                      <Col sm={6} key={k}>
                        <label className="form-label">{p}</label>
                        <input type={t} className="form-control" placeholder={p.replace(' *','')} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} required={p.includes('*')} style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                      </Col>
                    ))}
                    <Col xs={12}>
                      <label className="form-label">Product / Solution of Interest</label>
                      <select className="form-select" value={form.product_id} onChange={e=>{const sel=e.target.options[e.target.selectedIndex];setForm(f=>({...f,product_id:e.target.value,product_name:sel.text==='Select a product…'?'':sel.text}));}} style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}}>
                        <option value="">Select a product…</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name.replace(/\bPlatform\b/i, 'Services')}</option>)}
                      </select>
                    </Col>
                    <Col xs={12}>
                      <label className="form-label">Subject</label>
                      <input type="text" className="form-control" placeholder="Subject" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                    </Col>
                    <Col xs={12}>
                      <label className="form-label">Your Message *</label>
                      <textarea className="form-control" rows={5} placeholder="Your message..." value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} required style={{ resize:'vertical', color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                    </Col>
                    <Col xs={12}>
                      <Turnstile
                        ref={captchaRef}
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={() => setCaptchaOk(true)}
                        onExpire={() => setCaptchaOk(false)}
                      />
                    </Col>
                    <Col xs={12}>
                      <button type="submit" disabled={submitting || !captchaOk} className="btn-cyber w-100" style={{ padding:'0.85rem', fontSize:'1rem', borderRadius:10 }}>
                        {submitting ? <><span className="spinner-border spinner-border-sm me-2" />Sending…</> : 'Send Inquiry'}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>

            <Col lg={5}>
              <div>
                {/* Contact Info */}
                <div style={{ background:'#ffffff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-lg)', padding:'2rem', marginBottom:'1.5rem', boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
                  <h5 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'1.5rem' }}>Contact Information</h5>
                  {[
                    [FaMapMarkerAlt,'Address','Unit 33 15 Ricketts Rd, Mount Waverley VIC 3149, Australia'],
                    [FaPhone,'Phone','(03) 9543 7566'],
                    [FaEnvelope,'Email','sales@softlogic.com.au'],
                    [FaClock,'Business Hours','Monday – Friday: 9:00am – 5:30pm AEST'],
                  ].map(([Icon, label, val]) => (
                    <div key={label} className="d-flex gap-3 mb-3">
                      <div style={{ width:40, height:40, borderRadius:10, background:'#eff6ff', border:'1px solid rgba(0,102,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <Icon style={{ color:'#0066ff', fontSize:'0.9rem' }} />
                      </div>
                      <div>
                        <div style={{ color:'#64748b', fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.2rem' }}>{label}</div>
                        <div style={{ color:'#060d1a', fontSize:'0.9rem', fontWeight:600 }}>{val}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid #e2e8f0' }}>
                    <div style={{ display:'flex', gap:10 }}>
                      {[[FaFacebook,'https://www.facebook.com/AustraliaSoftlogic'],[FaLinkedin,'https://www.linkedin.com/company/australia-softlogic/']].map(([Icon,href],i) => (
                        <a key={i} href={href} target="_blank" rel="noreferrer" style={{ width:36, height:36, borderRadius:8, border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', transition:'all 0.2s' }}
                          onMouseEnter={e=>{e.currentTarget.style.color='#0066ff';e.currentTarget.style.borderColor='#0066ff';}}
                          onMouseLeave={e=>{e.currentTarget.style.color='#64748b';e.currentTarget.style.borderColor='#e2e8f0';}}>
                          <Icon />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Why Contact Us */}
                <div style={{ background:'#f0f9ff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-md)', padding:'1.5rem', marginBottom:'1.5rem' }}>
                  <h6 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'1rem' }}>Why Choose Softlogic?</h6>
                  {['25+ years industry experience','Custom solutions for your business','Dedicated support team','Flexible implementation','Best in business guarantee'].map(item => (
                    <div key={item} className="d-flex align-items-center gap-2 mb-2">
                      <FaCheck style={{ color:'#0066ff', flexShrink:0, fontSize:'0.8rem' }} />
                      <span style={{ color:'#64748b', fontSize:'0.85rem' }}>{item}</span>
                    </div>
                  ))}
                </div>

                {/* Existing customer support */}
                <div style={{ background:'#0066ff', borderRadius:'var(--r-md)', padding:'1.5rem' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:'0.75rem' }}>
                    <div style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <FaHeadset style={{ color:'#fff', fontSize:'1rem' }} />
                    </div>
                    <h6 style={{ fontFamily:'var(--font-display)', color:'#fff', fontWeight:700, margin:0, fontSize:'0.95rem' }}>Existing Customer?</h6>
                  </div>
                  <p style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.83rem', lineHeight:1.6, marginBottom:'1rem' }}>
                    Already using a Softlogic product? Submit a support query and our helpdesk team will assist you promptly.
                  </p>
                  <Link to="/support"
                    style={{ display:'block', textAlign:'center', padding:'0.65rem 1rem', background:'#fff', color:'#0066ff', borderRadius:8, fontFamily:'var(--font-display)', fontWeight:700, fontSize:'0.88rem', textDecoration:'none', transition:'opacity 0.2s' }}
                    onMouseEnter={e=>e.currentTarget.style.opacity='0.9'}
                    onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                    Submit a Support Query
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

// ─── Stats Cycler ─────────────────────────────────────────────────────────────
const ABOUT_STATS = [
  { num:'25+',  label:'Years of Experience', icon:FaAward,   color:'#0066ff', bg:'rgba(0,102,255,0.07)' },
  { num:'200+', label:'Happy Clients',        icon:FaUsers,   color:'#7b61ff', bg:'rgba(123,97,255,0.07)' },
  { num:'500+', label:'Projects Delivered',   icon:FaRocket,  color:'#f5a623', bg:'rgba(245,166,35,0.07)'  },
  { num:'99%',  label:'Client Satisfaction',  icon:FaStar,    color:'#2ecc71', bg:'rgba(46,204,113,0.07)'  },
];

function StatsCycler() {
  const [current, setCurrent] = React.useState(0);
  const [anim, setAnim]       = React.useState(true);
  React.useEffect(() => {
    const t = setInterval(() => {
      setAnim(false);
      setTimeout(() => { setCurrent(c => (c + 1) % ABOUT_STATS.length); setAnim(true); }, 300);
    }, 2500);
    return () => clearInterval(t);
  }, []);
  const s = ABOUT_STATS[current];
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Featured cycling stat */}
      <div style={{ padding:'2rem', textAlign:'center' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', opacity: anim?1:0, transform: anim?'translateY(0)':'translateY(12px)', transition:'all 0.3s ease' }}>
          <s.icon style={{ fontSize:'2.5rem', color:s.color }} />
        </div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'3.5rem', fontWeight:900, color:s.color, lineHeight:1, opacity: anim?1:0, transform: anim?'translateY(0)':'translateY(12px)', transition:'all 0.3s ease 0.05s' }}>{s.num}</div>
        <div style={{ color:'#475569', fontSize:'1rem', fontWeight:600, marginTop:8, opacity: anim?1:0, transition:'opacity 0.3s ease 0.1s' }}>{s.label}</div>
      </div>
      {/* Dot indicators + mini stats row */}
      <div style={{ display:'flex', justifyContent:'center', gap:8 }}>
        {ABOUT_STATS.map((st, i) => (
          <button key={i} onClick={() => { setAnim(false); setTimeout(()=>{ setCurrent(i); setAnim(true); },300); }}
            style={{ width: i===current?28:8, height:8, borderRadius:4, border:'none', cursor:'pointer', padding:0, transition:'all 0.3s', background: i===current ? s.color : 'rgba(0,102,255,0.15)' }} />
        ))}
      </div>
      {/* Mini stat pills */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {ABOUT_STATS.map((st, i) => (
          <div key={i} onClick={() => { setAnim(false); setTimeout(()=>{ setCurrent(i); setAnim(true); },300); }}
            style={{ background: i===current ? st.bg : '#f8fafc', border:`1px solid ${i===current ? st.color+'40' : '#e2e8f0'}`, borderRadius:12, padding:'0.75rem 1rem', display:'flex', alignItems:'center', gap:10, cursor:'pointer', transition:'all 0.25s' }}>
            <div style={{ width:32, height:32, borderRadius:8, background:`${st.color}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <st.icon style={{ fontSize:'1rem', color:st.color }} />
            </div>
            <div>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:'1.1rem', color: i===current ? st.color : '#334155' }}>{st.num}</div>
              <div style={{ fontSize:'0.72rem', color:'#64748b', fontWeight:500 }}>{st.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── About Page ───────────────────────────────────────────────────────────────
export function AboutPage() {
  return (
    <>
      <SEO title="About Us — 25 Years of Innovation" description="Learn about Softlogic Australia's 25+ year journey delivering cutting-edge software solutions. Meet our team and discover our mission." url="/about" />
      <div className="page-hero">
        <Container>
          <div className="tag-label">Our Story</div>
          <h1 className="mt-2" style={{ letterSpacing:'-0.02em', lineHeight:1.1 }}>
            <span style={{ color:'#ffffff' }}>About </span>
            <span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent' }}>Softlogic</span>
            <span style={{ color:'#ffffff' }}> Australia</span>
          </h1>
          <p>Where innovation meets efficiency — 25+ years of transforming businesses through technology</p>
        </Container>
      </div>
      <section style={{ background:'var(--bg-white)', padding:'80px 0' }}>
        <Container>
          <Row className="align-items-center g-5 mb-5">
            <Col lg={6}>
              <div className="tag-label">Who We Are</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.8rem,3.5vw,2.5rem)', margin:'0.75rem 0 1.25rem', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em' }}>Softlogic Australia Pty Ltd</h2>
              <p style={{ color:'#64748b', lineHeight:1.9, marginBottom:'1rem' }}>
                Softlogic Australia is a software solution provider developing cutting-edge tech solutions that are flexible and robust. We aim to provide solutions that go beyond your expectations to meet your unique business requirements.
              </p>
              <p style={{ color:'#64748b', lineHeight:1.9, marginBottom:'1rem' }}>
                With a highly skilled team of developers and dedicated staff, we commit to excellence and help to empower your business and make it reach new heights in the digital landscape.
              </p>
              <p style={{ color:'#64748b', lineHeight:1.9 }}>
                Softlogic Australia aims to provide customizable software solutions for each of our customers. With an experience of over 25 years in the industry we have helped many clients to excel in their business. Our value proposition is <strong style={{ color:'var(--sky-dark)' }}>"Best in Business"</strong> and here at Softlogic Australia we ensure and strive to provide our customers with the best software solutions and services.
              </p>
            </Col>
            <Col lg={6}>
              <StatsCycler />
            </Col>
          </Row>

          {/* Services */}
          <div style={{ marginTop:'4rem' }}>
            <div className="section-header">
              <div className="tag-label">Area of Services</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.75rem,4vw,2.6rem)', marginBottom:'0.75rem', letterSpacing:'-0.02em', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>What We Do</h2>
            </div>
            <Row className="g-3">
              {[
                { icon: MdRocketLaunch, title:'Digital Transformation', desc:'We help businesses of all sizes embrace digital transformation through custom software solutions, automation, and cloud services.' },
                { icon: MdSecurity,     title:'Healthcare Technology',   desc:'Specialised software solutions for hospitals, aged care facilities, and healthcare providers including Chefmax and Facility Management systems.' },
                { icon: MdDevicesOther, title:'IoT & Smart Solutions',   desc:'Connected device solutions for asset monitoring, smart homes, and industrial automation through our Right IOT platform.' },
              ].map((item, i) => (
                <Col key={i} xs={12} sm={6} lg={4} data-aos-delay={i*80}>
                  <div className="glass-card h-100 text-center" style={{ padding:'1.5rem 1.25rem' }}>
                    <div style={{ width:52, height:52, borderRadius:'var(--r-sm)', background:'var(--sky-bg)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem' }}>
                      <item.icon style={{ color:'var(--sky-dark)', fontSize:'1.5rem' }} />
                    </div>
                    <h6 style={{ fontFamily:'var(--font-display)', color:'#060d1a', marginBottom:'0.5rem', fontSize:'0.95rem', fontWeight:700 }}>{item.title}</h6>
                    <p style={{ color:'#64748b', fontSize:'0.82rem', lineHeight:1.75, margin:0 }}>{item.desc}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </div>

          {/* CTA */}
          <div style={{ marginTop:'3rem' }}>
            <div style={{ background:'#f0f9ff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-xl)', padding:'3rem', textAlign:'center', boxShadow:'0 2px 16px rgba(0,102,255,0.08)' }}>
              <div className="tag-label" style={{ margin:'0 auto 1rem', width:'fit-content', background:'rgba(0,102,255,0.08)', borderColor:'rgba(0,102,255,0.25)', color:'#0066ff' }}>
                Get in Touch
              </div>
              <h3 style={{ fontFamily:'var(--font-display)', marginBottom:'0.75rem', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em' }}>
                Ready to Elevate Your Business?
              </h3>
              <p style={{ color:'#64748b', maxWidth:480, margin:'0 auto 1.75rem', lineHeight:1.8 }}>
                Contact us now and let us help you elevate your business to new heights with our advanced technology solutions.
              </p>
              <Link to="/contact" className="btn-cyber" style={{ padding:'0.75rem 2rem', fontSize:'0.95rem', display:'inline-flex', alignItems:'center', gap:8 }}>
                Get in Touch <FaArrowRight />
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

export default ContactPage;

// ─── Inquiry Page ─────────────────────────────────────────────────────────────
export function InquiryPage() {
  const [form, setForm] = React.useState({ full_name:'', email:'', phone:'', company:'', subject:'', message:'', product_name:'' });
  const [products, setProducts] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const captchaRef  = useRef(null);
  const [captchaOk, setCaptchaOk] = React.useState(false);
  const { productAPI: pAPI } = require('../../utils/api');

  React.useEffect(() => {
    productAPI.getAll({ limit: 50 }).then(r => setProducts(r.data.data || [])).catch(() => {});
  }, []);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Name required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.message.trim()) e.message = 'Message required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!captchaOk) { toast.error('Please complete the CAPTCHA verification'); return; }
    setSubmitting(true);
    try {
      await inquiryAPI.submit(form);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitted(true);
      captchaRef.current?.reset();
      setCaptchaOk(false);
      toast.success("Inquiry submitted! We'll contact you within 1-2 business days.");
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit. Please try again.');
      captchaRef.current?.reset();
      setCaptchaOk(false);
    }
    setSubmitting(false);
  };

  if (submitted) return (
    <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f1f5f9' }}>
      <Container>
        <div className="text-center py-5" style={{ background:'#ffffff', borderRadius:20, padding:'3rem', boxShadow:'0 2px 24px rgba(0,0,0,0.06)', border:'1px solid #e2e8f0', maxWidth:560, margin:'0 auto' }}>
          <div style={{ fontSize:'5rem', marginBottom:'1.5rem', color:'#16a34a' }}><FaCheckCircle /></div>
          <h2 style={{ fontFamily:'var(--font-display)', color:'#060d1a', marginBottom:'1rem' }}>Inquiry Submitted!</h2>
          <p style={{ color:'#64748b', fontSize:'1.1rem', maxWidth:500, margin:'0 auto 2rem' }}>
            Thank you for reaching out! Our team will review your inquiry and contact you within 1–2 business days.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/products" className="btn-cyber" style={{ padding:'0.75rem 2rem', border:'2px solid #0043cc' }}>Explore Solutions</Link>
            <Link to="/" className="btn-cyber" style={{ padding:'0.75rem 2rem', border:'2px solid #0043cc' }}>Back to Home</Link>
          </div>
        </div>
      </Container>
    </div>
  );

  return (
    <>
      <SEO title="Submit an Inquiry" description="Get in touch with Softlogic Australia. Submit your inquiry and our team will respond within 1-2 business days." url="/inquiry" />
      <div className="page-hero">
        <Container>
          <div className="tag-label"><FaEnvelopeOpen style={{ marginRight:5 }} /> Contact Sales</div>
          <h1 className="mt-2" style={{ letterSpacing:'-0.02em', lineHeight:1.1 }}>
            <span style={{ color:'#ffffff' }}>Submit an </span>
            <span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent' }}>Inquiry</span>
          </h1>
          <p>Tell us about your requirements and we'll craft the perfect solution for you</p>
        </Container>
      </div>
      <section style={{ background:'#ffffff', padding:'80px 0' }}>
        <Container>
          <Row className="g-5">
            <Col lg={8}>
              <div style={{ background:'#ffffff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-lg)', padding:'2.5rem', boxShadow:'0 2px 16px rgba(0,0,0,0.05)' }}>
                <h4 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'0.5rem' }}>Send Us a Message</h4>
                <p style={{ color:'#64748b', fontSize:'0.9rem', marginBottom:'2rem' }}>Fill in the form and our team will get back to you promptly.</p>
                <form onSubmit={handleSubmit} className="form-light">
                  <Row className="g-3">
                    <Col sm={6}>
                      <label className="form-label" style={{ color:'#334155' }}>Full Name *</label>
                      <input className={`form-control ${errors.full_name?'border-danger':''}`} value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} placeholder="Full Name" style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                      {errors.full_name && <div style={{color:'#ff6b6b',fontSize:'0.8rem',marginTop:4}}>{errors.full_name}</div>}
                    </Col>
                    <Col sm={6}>
                      <label className="form-label">Email Address *</label>
                      <input type="email" className={`form-control ${errors.email?'border-danger':''}`} value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="Email Address" style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                      {errors.email && <div style={{color:'#ff6b6b',fontSize:'0.8rem',marginTop:4}}>{errors.email}</div>}
                    </Col>
                    <Col sm={6}>
                      <label className="form-label">Phone Number</label>
                      <input className="form-control" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="Phone Number" style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                    </Col>
                    <Col sm={6}>
                      <label className="form-label">Company Name</label>
                      <input className="form-control" value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))} placeholder="Company Name" style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                    </Col>
                    <Col sm={6}>
                      <label className="form-label">Product of Interest</label>
                      <select className="form-control" value={form.product_name} onChange={e=>setForm(f=>({...f,product_name:e.target.value}))} style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}}>
                        <option value="">Select a product…</option>
                        {products.map(p=><option key={p.id} value={p.name.replace(/\bPlatform\b/i,'Services')}>{p.name.replace(/\bPlatform\b/i,'Services')}</option>)}
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </Col>
                    <Col sm={6}>
                      <label className="form-label">Subject</label>
                      <input className="form-control" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))} placeholder="Subject" style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                    </Col>
                    <Col xs={12}>
                      <label className="form-label">Your Message *</label>
                      <textarea rows={5} className={`form-control ${errors.message?'border-danger':''}`} value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} placeholder="Your message..." style={{ color:'#060d1a', WebkitTextFillColor:'#060d1a', background:'#ffffff' }} onFocus={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';e.target.style.background='#ffffff';}} onInput={e=>{e.target.style.color='#060d1a';e.target.style.WebkitTextFillColor='#060d1a';}} />
                      {errors.message && <div style={{color:'#ff6b6b',fontSize:'0.8rem',marginTop:4}}>{errors.message}</div>}
                    </Col>
                    <Col xs={12}>
                      <Turnstile
                        ref={captchaRef}
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={() => setCaptchaOk(true)}
                        onExpire={() => setCaptchaOk(false)}
                      />
                    </Col>
                    <Col xs={12}>
                      <button type="submit" className="btn-cyber w-100 py-3" disabled={submitting || !captchaOk} style={{fontSize:'1rem'}}>
                        {submitting ? 'Sending...' : <><FaRocket style={{ marginRight:6 }} />Send Inquiry</>}
                      </button>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>
            <Col lg={4}>
              <div style={{ position:'sticky', top:120 }}>
                <div style={{ background:'#ffffff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-lg)', padding:'2rem', marginBottom:'1.5rem', boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
                  <h5 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'1.5rem' }}>Contact Information</h5>
                  {[[FaPhone,'(03) 9543 7566','tel:0395437566'],[FaEnvelope,'sales@softlogic.com.au','mailto:sales@softlogic.com.au'],[FaMapMarkerAlt,'Unit 33 15 Ricketts Rd, Mount Waverley VIC 3149',null]].map(([Icon,text,href],i)=>(
                    <div key={i} style={{ display:'flex', gap:12, marginBottom:16, alignItems:'flex-start' }}>
                      <div style={{ width:36, height:36, borderRadius:8, background:'#eff6ff', border:'1px solid rgba(0,102,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <Icon style={{ color:'#0066ff' }} />
                      </div>
                      {href ? <a href={href} style={{ color:'#060d1a', fontSize:'0.9rem', marginTop:6, textDecoration:'none', fontWeight:600 }}>{text}</a>
                             : <span style={{ color:'#64748b', fontSize:'0.88rem', marginTop:4, lineHeight:1.5 }}>{text}</span>}
                    </div>
                  ))}
                </div>
                <div style={{ background:'#f0f9ff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-md)', padding:'1.5rem' }}>
                  <h6 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', letterSpacing:'-0.02em', marginBottom:'1rem' }}>Why Choose Softlogic?</h6>
                  {['25+ years of industry experience','Customized solutions for your needs','Expert team of developers','24/7 dedicated support','Best in business quality'].map(item=>(
                    <div key={item} style={{ display:'flex', gap:10, marginBottom:10, alignItems:'center' }}>
                      <FaCheck style={{ color:'#0066ff', flexShrink:0, fontSize:'0.75rem' }} />
                      <span style={{ color:'#64748b', fontSize:'0.88rem' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

// ─── Product Detail Page ──────────────────────────────────────────────────────
export function ProductDetailPage() {
  const { useParams } = require('react-router-dom');
  const { slug } = useParams();
  const [product, setProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    productAPI.getBySlug(slug).then(r => { setProduct(r.data.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ width:48, height:48, border:'3px solid rgba(0,212,255,0.2)', borderTopColor:'var(--sky)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /></div>;
  if (!product) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', color:'#060d1a' }}><div className="text-center"><h4>Product not found</h4><Link to="/products" className="btn-cyber mt-3" style={{ display:'inline-block', padding:'0.65rem 2rem' }}>Back to Products</Link></div></div>;

  return (
    <>
      <SEO title={product.meta_title || product.name} description={product.meta_desc || product.short_desc} keywords={product.meta_keywords} url={`/products/${product.slug}`} image={product.cover_image} type="product" />
      <div style={{ height:'65vh', background:`linear-gradient(to bottom, rgba(10,28,60,0.5) 0%, rgba(10,28,60,0.8) 100%), url(${product.cover_image||'/images/fallback-hero.jpg'}) center/cover`, display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', position:'relative' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize:'28px 28px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3, background:'linear-gradient(90deg, transparent, var(--sky), transparent)' }} />
        <Container style={{ position:'relative' }}>
          <div style={{ display:'inline-block', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', borderRadius:100, padding:'0.25rem 1.25rem', fontSize:'0.75rem', fontWeight:700, marginBottom:'1.25rem', letterSpacing:'0.12em', textTransform:'uppercase' }}>{product.category}</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2.2rem,5vw,3.8rem)', marginBottom:'1rem', fontWeight:800, letterSpacing:'-0.02em', lineHeight:1.1, color:'#ffffff' }}>
            {product.name.split(' ').length > 1
              ? <><span style={{ color:'#ffffff' }}>{product.name.split(' ').slice(0,-1).join(' ')} </span><span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent', color:'transparent' }}>{product.name.split(' ').slice(-1)[0]}</span></>
              : <span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent', color:'transparent' }}>{product.name}</span>
            }
          </h1>
          {product.short_desc && <p style={{ color:'rgba(255,255,255,0.7)', fontSize:'1.1rem', maxWidth:580, margin:'0 auto', lineHeight:1.8, textShadow:'0 1px 8px rgba(0,0,0,0.4)' }}>{product.short_desc}</p>}
        </Container>
      </div>
      <section style={{ background:'#ffffff', padding:'80px 0' }}>
        <Container>
          <Row className="g-5">
            <Col lg={8}>
              {product.description && (
                <div
                  className="product-rich-content"
                  style={{ marginBottom:'2rem' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolveHtmlImages(product.description)) }}
                />
              )}
              {Array.isArray(product.features) && product.features.length > 0 && (
                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'var(--r-md)', padding:'2rem', marginBottom:'2rem' }}>
                  <h4 style={{ fontFamily:'var(--font-display)', color:'#060d1a', marginBottom:'1.5rem' }}>Key Features</h4>
                  <Row className="g-3">
                    {product.features.map((f,i) => (
                      <Col key={i} sm={6}>
                        <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                          <div style={{ width:24, height:24, borderRadius:'50%', background:'rgba(0,212,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}><FaCheck style={{ color:'var(--sky-dark)', fontSize:'0.65rem' }} /></div>
                          <span style={{ color:'#060d1a', fontSize:'0.92rem' }}>{f}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Col>
            <Col lg={4}>
              <div style={{ position:'sticky', top:120, background:'#ffffff', border:'1px solid rgba(0,102,255,0.2)', borderRadius:'var(--r-lg)', padding:'2rem', boxShadow:'0 2px 16px rgba(0,0,0,0.06)' }}>
                <h5 style={{ fontFamily:'var(--font-display)', background:'linear-gradient(135deg, #0f172a 0%, #0066ff 50%, #00d4ff 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', marginBottom:'1.5rem' }}>Get Started</h5>
                <Link to={`/inquiry?product=${encodeURIComponent(product.name)}`} className="btn-cyber d-block text-center py-3 mb-3" style={{ fontSize:'1rem' }}>Request a Demo</Link>
                <Link to="/contact" style={{ display:'block', textAlign:'center', padding:'0.6rem', marginBottom:'1rem', border:'2px solid #0066ff', borderRadius:'var(--r-sm)', color:'#0066ff', fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.92rem', textDecoration:'none', transition:'all 0.2s' }}
                  onMouseEnter={e=>{e.currentTarget.style.background='#0066ff';e.currentTarget.style.color='#fff';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='#0066ff';}}>Contact Sales</Link>
                <div style={{ borderTop:'1px solid #e2e8f0', paddingTop:'1.5rem' }}>
                  {[[FaPhone,'(03) 9543 7566','tel:0395437566'],[FaEnvelope,'sales@softlogic.com.au','mailto:sales@softlogic.com.au']].map(([Icon,text,href])=>(
                    <a key={href} href={href} style={{ display:'flex', gap:10, alignItems:'center', color:'#334155', fontSize:'0.88rem', marginBottom:12, textDecoration:'none' }}>
                      <Icon style={{ color:'#0284c7' }} />{text}
                    </a>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

// ─── News Detail Page ─────────────────────────────────────────────────────────
export function NewsDetailPage() {
  const { useParams } = require('react-router-dom');
  const { slug } = useParams();
  const [article, setArticle] = React.useState(null);
  const [loading, setLoading]  = React.useState(true);
  const { newsAPI } = require('../../utils/api');

  React.useEffect(() => {
    newsAPI.getBySlug(slug).then(r => { setArticle(r.data.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ width:48, height:48, border:'3px solid rgba(0,212,255,0.2)', borderTopColor:'var(--sky)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /></div>;
  if (!article) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', color:'#060d1a' }}><div className="text-center"><h4>Article not found</h4><Link to="/news" className="btn-cyber mt-3" style={{ display:'inline-block', padding:'0.65rem 2rem' }}>Back to News</Link></div></div>;

  return (
    <>
      <SEO title={article.meta_title || article.title} description={article.meta_desc || article.excerpt} keywords={article.meta_keywords || article.tags} url={`/news/${article.slug}`} image={article.cover_image} type="article" />
      <div style={{ height:'65vh', background:`linear-gradient(to bottom, rgba(10,28,60,0.5) 0%, rgba(10,28,60,0.82) 100%), url(${article.cover_image || '/images/fallback-hero.jpg'}) center/cover`, display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', position:'relative' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize:'28px 28px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3, background:'linear-gradient(90deg, transparent, var(--sky), transparent)' }} />
        <Container style={{ position:'relative' }}>
          {article.category && <div style={{ display:'inline-block', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', borderRadius:100, padding:'0.25rem 1.25rem', fontSize:'0.75rem', fontWeight:700, marginBottom:'1.25rem', letterSpacing:'0.1em', textTransform:'uppercase' }}>{article.category}</div>}
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(2rem,4.5vw,3.5rem)', maxWidth:800, margin:'0 auto 1rem', fontWeight:800, letterSpacing:'-0.02em', lineHeight:1.1, color:'#ffffff' }}>
            {article.title.split(' ').length > 2
              ? <><span style={{ color:'#ffffff' }}>{article.title.split(' ').slice(0,-2).join(' ')} </span><span style={{ WebkitTextStroke:'2px #00d4ff', WebkitTextFillColor:'transparent', color:'transparent' }}>{article.title.split(' ').slice(-2).join(' ')}</span></>
              : <span style={{ color:'#ffffff' }}>{article.title}</span>
            }
          </h1>
          <div style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.9rem', textShadow:'0 1px 8px rgba(0,0,0,0.3)' }}>By {article.author_name} · {article.published_at ? new Date(article.published_at).toLocaleDateString('en-AU',{year:'numeric',month:'long',day:'numeric'}) : ''}</div>
        </Container>
      </div>
      <section className="section" style={{ background:'var(--bg-white)' }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div style={{ color:'#060d1a', lineHeight:1.9, fontSize:'1.05rem' }} dangerouslySetInnerHTML={{ __html: article.content }} />
              <div style={{ marginTop:'3rem', paddingTop:'2rem', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                <Link to="/news" style={{ color:'var(--sky-dark)', fontWeight:600, fontSize:'0.9rem' }}>← Back to News</Link>
                <Link to="/contact" className="btn-cyber" style={{ padding:'0.6rem 1.5rem', fontSize:'0.9rem' }}>Contact Us</Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}
